import clsx from 'clsx'
import {
  type PropsWithChildren,
  type ReactElement,
  useRef,
  useState
} from 'react'
import { useGetUserToken } from '~/api/mutations/login'
import { useMeInfos } from '~/api/queries/user'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { container } from '~/components/Global/Interactions/Dropdown/Downdown.css'
import { Dropdown } from '~/components/Global/Interactions/Dropdown/Dropdown'
import { useConfig } from '~/domains/configuration/queries/config'
import { UsersAutocomplete } from '~/domains/users/components/UsersAutocomplete'
import { formatName } from '~/utils/names'
import { switchBackButton } from './UserSwitcher.css'

type UserSwitcherProps = PropsWithChildren<{
  classname?: string
}>

export const UserSwitcher = ({
  classname,
  children
}: UserSwitcherProps): ReactElement => {
  const [isOpen, setOpen] = useState<boolean>(false)
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [userSwitching, setUserSwitching] = useState<boolean>(false)
  const getUserToken = useGetUserToken()
  const userInfos = useMeInfos()
  const switchUser = async (userId: string): Promise<{ token: string }> => {
    setOpen(false)
    setUserSwitching(true)
    return await getUserToken.mutateAsync(
      { id: userId },
      {
        onSuccess: async ({ token }) => {
          localStorage.setItem('mainToken', localStorage.getItem('token') ?? '')
          localStorage.setItem('mainUser', formatName(userInfos))
          localStorage.setItem('mainId', `${userInfos?.id ?? ''}`)
          localStorage.setItem('token', token)
          localStorage.setItem('id', userId)
          localStorage.removeItem('currentGroup')
          // Handle legacy change user modale and return to the current page
          window.location.href = `${legacyUrl ?? ''}/admin/users/swap_user/${userId}?returnTo=${window.location.href}`
        }
      }
    )
  }

  const switchBack = async (): Promise<void> => {
    setUserSwitching(true)
    setOpen(false)
    const mainId = localStorage.getItem('mainId')
    localStorage.setItem('token', localStorage.getItem('mainToken') ?? '')
    localStorage.setItem('id', mainId ?? '')
    localStorage.removeItem('mainToken')
    localStorage.removeItem('mainUser')
    localStorage.removeItem('mainId')
    localStorage.removeItem('currentGroup')
    // Handle legacy change user modale and return to the current page
    window.location.href = `${legacyUrl ?? ''}/admin/users/swap_user/${mainId ?? ''}?returnTo=${window.location.href}`
  }

  if (getUserToken.isPending || userSwitching) {
    return <Loader small={true} />
  }

  return (
    <>
      {localStorage.getItem('mainToken') !== null && (
        <span onClick={switchBack} className={switchBackButton}>
          Retour vers {localStorage.getItem('mainUser')}
        </span>
      )}
      {userInfos?.isSuperUser === true && (
        <div className={clsx(classname, container)} ref={dropdownRef}>
          <span
            onClick={() => {
              setOpen(!isOpen)
            }}
          >
            {children}
          </span>
          <Dropdown
            isOpen={isOpen}
            onclose={() => setOpen(false)}
            dropdownRef={dropdownRef}
            offsetValue={25}
          >
            <UsersAutocomplete
              inlineList
              small
              fullWidth
              placeholder="Rechercher un utilisateur"
              onChange={async (nextUserId) => {
                if (nextUserId === '') {
                  return
                }
                await switchUser(nextUserId)
              }}
            />
          </Dropdown>
        </div>
      )}
    </>
  )
}
